import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const OTAS = ['mmt', 'agoda', 'yatra'];
const LABEL = { mmt: 'MMT', agoda: 'Agoda', yatra: 'Yatra' };

const hdr = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const Pill = ({ ota, enabled, onToggle, disabled, scope }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onToggle}
    data-testid={`ota-sync-${scope}-${ota}`}
    data-enabled={enabled ? 'true' : 'false'}
    title={`${LABEL[ota]} sync ${enabled ? 'ON' : 'OFF'} — click to turn ${enabled ? 'OFF' : 'ON'}`}
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors disabled:opacity-50 ${
      enabled
        ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
        : 'bg-gray-100 border-gray-300 text-gray-500'
    }`}
  >
    <span className={`w-2 h-2 rounded-full ${enabled ? 'bg-emerald-500' : 'bg-gray-400'}`} />
    {LABEL[ota]} {enabled ? 'ON' : 'OFF'}
  </button>
);

// Per-property OTA sync toggles (Calendar tab). Reads the property's stored
// state on mount and persists each change immediately. Independent per property.
export const OtaSyncToggles = ({ apiBase, propertyId }) => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    if (!propertyId) return;
    try {
      const res = await axios.get(`${apiBase}/properties/${propertyId}/ota-sync`, hdr());
      setState(res.data?.ota_sync || { mmt: true, agoda: true, yatra: true });
    } catch {
      setState({ mmt: true, agoda: true, yatra: true });
    }
  }, [apiBase, propertyId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (ota) => {
    if (!state) return;
    const next = !state[ota];
    setBusy(ota);
    setState(s => ({ ...s, [ota]: next }));           // optimistic
    try {
      await axios.put(`${apiBase}/properties/${propertyId}/ota-sync`, { ota, enabled: next }, hdr());
      toast.success(`${LABEL[ota]} ARI sync ${next ? 'enabled' : 'disabled'} for this property`);
    } catch (e) {
      setState(s => ({ ...s, [ota]: !next }));         // revert
      toast.error(e.response?.data?.detail || `Failed to update ${LABEL[ota]} sync`);
    } finally {
      setBusy('');
    }
  };

  if (!state) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="calendar-ota-sync-toggles">
      <span className="text-xs text-gray-500 font-medium">OTA Sync:</span>
      {OTAS.map(ota => (
        <Pill key={ota} ota={ota} enabled={!!state[ota]} disabled={busy === ota}
          scope="property" onToggle={() => toggle(ota)} />
      ))}
    </div>
  );
};

// Bulk OTA sync control (All Properties → Settings). Shows the AGGREGATE state
// across all properties using ALL logic: a toggle shows ON only when EVERY
// property has that OTA enabled; if any property has it OFF it shows "Mixed",
// and OFF when none are enabled. Clicking writes the new value to EVERY property
// at that moment, then re-reads the aggregate. It never writes on page load.
export const BulkOtaSyncToggles = ({ apiBase }) => {
  const [summary, setSummary] = useState(null);  // { ota: {all_enabled, mixed, enabled_count, total} }
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/properties/ota-sync/summary`, hdr());
      setSummary(res.data?.summary || null);
    } catch {
      setSummary(null);
    }
  }, [apiBase]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (ota) => {
    const cur = summary?.[ota]?.all_enabled;
    const next = !cur;   // if not ALL enabled (off/mixed) -> enable all; else disable all
    setBusy(ota);
    try {
      const res = await axios.put(`${apiBase}/properties/ota-sync/bulk`, { ota, enabled: next }, hdr());
      toast.success(`${LABEL[ota]} ARI sync ${next ? 'enabled' : 'disabled'} for all ${res.data?.updated_count ?? ''} properties`);
      await load();   // re-read aggregate so the pill reflects the true state
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to bulk update ${LABEL[ota]} sync`);
    } finally {
      setBusy('');
    }
  };

  if (!summary) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="bulk-ota-sync-toggles">
      {OTAS.map(ota => {
        const s = summary[ota] || {};
        const label = s.mixed ? 'Mixed' : (s.all_enabled ? 'ON' : 'OFF');
        const cls = s.mixed
          ? 'bg-amber-50 border-amber-300 text-amber-700'
          : (s.all_enabled ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                           : 'bg-gray-100 border-gray-300 text-gray-500');
        const dot = s.mixed ? 'bg-amber-500' : (s.all_enabled ? 'bg-emerald-500' : 'bg-gray-400');
        return (
          <button key={ota} type="button" disabled={busy === ota} onClick={() => toggle(ota)}
            data-testid={`ota-sync-bulk-${ota}`}
            data-enabled={s.all_enabled ? 'true' : 'false'}
            data-mixed={s.mixed ? 'true' : 'false'}
            title={`${LABEL[ota]}: ${s.enabled_count ?? 0}/${s.total ?? 0} properties enabled — click to turn ${s.all_enabled ? 'OFF' : 'ON'} for all`}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors disabled:opacity-50 ${cls}`}>
            <span className={`w-2 h-2 rounded-full ${dot}`} />
            {LABEL[ota]} {label}
          </button>
        );
      })}
    </div>
  );
};

export default OtaSyncToggles;
